import { gql } from 'graphql-request'

// Types for multilingual attributes
export interface SupportedAttribute {
  name: string
  displayName: string
  description: string
  sensitivityLabel: string
  isBeta: boolean
  systemDefined: boolean
  standardIdentifier: string
}

export interface MultilingualAttribute {
  id: string
  code: string
  name: string
  enabled: boolean
  supportedAttributes: SupportedAttribute[]
  attributesSupported: number
}

export interface LanguageStatusInput {
  code: string
  enabled: boolean
}

export interface UpdateLanguageStatusParams {
  languages: LanguageStatusInput[]
}

export interface UpdateLanguageStatusResponse {
  success: boolean
  updatedCount: number
  languages: {
    code: string
    language: string
    enabled: boolean
  }[]
}

// Query for fetching multilingual attributes
export const queryMultilingualAttributes = (): string => {
  return gql`
    query {
      multilingualAttributes {
        edges {
          node {
            id
            code
            language
            enabled
            supportedAttributes {
              name
              displayName
              description
              sensitivityLabel
              isBeta
              systemDefined
              standardIdentifier
            }
          }
        }
      }
    }
  `
}

// Mutation for updating language status
export const mutationUpdateLanguageStatus = (params: UpdateLanguageStatusParams): string => {
  const { languages } = params

  const formattedLanguages = languages
    .map(
      (lang) => `{
    code: "${lang.code}",
    enabled: ${lang.enabled}
  }`
    )
    .join(',')

  return gql`
    mutation {
      updateLanguageStatus(
        languages: [${formattedLanguages}]
      ) {
        success
        updatedCount
        languages {
          code
          language
          enabled
        }
      }
    }
  `
}

// Mapper for multilingual attributes query response
export const mapQueryMultilingualAttributes = (raw: any): MultilingualAttribute[] => {
  try {
    return (
      raw?.multilingualAttributes?.edges?.map((edge: any) => ({
        id: edge.node.id,
        code: edge.node.code,
        name: edge.node.language,
        enabled: edge.node.enabled,
        attributesSupported: edge.node.supportedAttributes.length,
        supportedAttributes: edge.node.supportedAttributes || []
      })) || []
    )
  } catch (error) {
    console.error('Error mapping multilingual attributes:', error)
    return []
  }
}

// Mapper for update language status mutation response
export const mapMutationUpdateLanguageStatus = (raw: any): UpdateLanguageStatusResponse => {
  try {
    return {
      success: true,
      updatedCount: raw?.data?.updateLanguageStatus?.updatedCount || 0,
      languages: raw?.data?.updateLanguageStatus?.languages || []
    }
  } catch (error) {
    console.error('Error mapping update language status response:', error)
    return {
      success: false,
      updatedCount: 0,
      languages: []
    }
  }
}
